<template>
  <div v-if="otjsReady && programmeId" id="programme-otj-logs">
    <k-panel
      :hasContentToggle="true"
      title="OTJ Progress"
      :showContent="showOtjProgress"
      @toggle="showOtjProgress = $event"
      >
      <template #body>
        <otj-progress-chart v-if="useNewOtjProgressChart" :user-id="userId" :programme-id="programmeId" class="otj-progress-chart"></otj-progress-chart>
        <otj-progress-bar-chart v-else :user-id="userId" :programme-id="programmeId" class="otj-progress-chart"></otj-progress-bar-chart>
      </template>
    </k-panel>
    <k-panel :hasContentToggle="false">
      <template #body>
        <div class="panel-heading">
          <div class="header-left">
            <otj-progress :planned-otj-hours="plannedOTJHours" :total-time-spent="totalTimeSpent"></otj-progress>
          </div>
          <div class="header-right">
            <button v-if="otjs.length > 0" class="btn btn-primary expand-table-btn" @click="expandTable">
              Expand Table <i class="fa-regular fa-arrows-maximize" aria-hidden="true"></i>
            </button>
            <k-dropdown-multi-select
              v-if="otjs.length > 0"
              v-model="selectedStatuses"
              :options="otjStatuses"
              :showSearch="false"
              :showFilterIcon="true"
              placeholder="Filter by Status"
            />
          </div>
        </div>
        <div v-if="otjs.length > 0" class="otj-table-container">
          <k-table
            class="otj-table"
            :headers="kableHeaders"
            :rows="kableRows"
            :hideable="false"
            :panel="false"
            :downloadable="true"
            :filename="otjTableFilename"
            :max="10"
            :enableTopScroll="true"
            :date-format="'dd/MM/yyyy'"
            :timestamp-format="'h:mm a'"
            @clicked="handleTableAction"
          ></k-table>
          <k-modal v-if="showExpandedTableModal" :show="showExpandedTableModal" @close="showExpandedTableModal = false">
            <template #header>
              <h3>O.T.J. Logs</h3>
            </template>
            <template #body>
              <k-table
                :headers="kableHeaders"
                :rows="kableRows"
                :hideable="false"
                :panel="false"
                :max="10"
                :enableTopScroll="true"
                :show-controls="false"
                @clicked="handleTableAction"
              ></k-table>
            </template>
            <template #footer>
              <button class="btn btn-primary" @click="showExpandedTableModal = false">Close</button>
            </template>
          </k-modal>
        </div>
        <empty-placeholder v-else
          title="No O.T.J. Logs Found"
          info="There are no O.T.J. logs for this programme yet."
        ></empty-placeholder>
      </template>
    </k-panel>
    <k-modal :show="showCoachNotesModal" @close="showCoachNotesModal = false">
      <template #header>
        <h3>Coach's Notes</h3>
      </template>
      <template #body>
        <p class="coach-notes-text">{{ coachNotes || 'No feedback provided.' }}</p>
      </template>
      <template #footer>
        <button class="btn btn-primary" @click="showCoachNotesModal = false">Close</button>
      </template>
    </k-modal>
    <add-edit-otj v-if="canEditOtj"
      :show-modal="showOtjModal"
      @close="showOtjModal = false"
      @update="getOtjLogs"
      :programme-id="programmeId"
      :learner-id="userId"
      :programme-member-id="programmeMemberId"
      :is-editing="isEditing"
      :otj-data="otjToEdit"
      :compliance-mode="$permissions.hasPermission('manage_user_otjs')"
    ></add-edit-otj>
    <add-comments-otj-modal
      :show-modal="showAddCommentsModal"
      @close="showAddCommentsModal = false"
      :otj-data="otjToAddComments"
      @update-status="setOtjStatus"
      :programme-id="programmeId"
      :programme-member-id="programmeMemberId"
      :action="addCommentsAction"
    ></add-comments-otj-modal>
  </div>
</template>

<style scoped>
.panel-heading {
  display: flex;
  justify-content: space-between;
  gap: 15px;
  align-items: center;
  margin-bottom: 20px;
}

.header-left,
.header-right {
  display: flex;
  gap: 10px;
}

.header-left {
  align-items: center;
}

.header-right {
  align-items: stretch;
}

.header-right button.expand-table-btn i {
  margin-left: 5px;
}

.vertical-hr {
  height: 20px;
  width: 1px;
  background-color: var(--kate-border);
  margin: 0 10px;
}

p.coach-notes-text {
  background-color: var(--input-background);
  padding: 10px;
  border-radius: 8px;
  border: var(--input-border-alt);
}

.otj-progress-chart {
  margin-top: 20px;
}
</style>

<script>
import KModal from '@base-components/k-modal.vue';
import KPanel from '@base-components/k-panel.vue';
import KTable from '@base-components/tables/display/k-table.vue';
import EmptyPlaceholder from '@base-components/empty-placeholder.vue';
import TimeMixin from '@mixins/time-mixins';
import useGlobalStore from '@stores/global';
import { sortObjectArray } from '@utils/sort-by-object-property';
import { compactFormatId } from '@utils/format-user-id';
import KDropdownMultiSelect from '@base-components/k-dropdown-multi-select.vue';
import AddEditOtj from '../../../programmes/otj_components/add-edit-otj.vue';
import AddCommentsOtjModal from '../../monitor/otjs/add-comments-otj-modal.vue';
import OtjProgress from '../../../programmes/otj_components/otj-progress.vue';
import OtjProgressChart from '../../../programmes/otj_components/otj-progress-chart.vue';
import OtjProgressBarChart from '../../../programmes/otj_components/otj-progress-bar-chart.vue';

export default {
  components: {
    OtjProgress,
    KModal,
    KPanel,
    KTable,
    AddEditOtj,
    EmptyPlaceholder,
    KDropdownMultiSelect,
    AddCommentsOtjModal,
    OtjProgressChart,
    OtjProgressBarChart,
  },

  mixins: [TimeMixin],

  props: {
    programmeId: Number,
    userId: Number,
    programmeMemberId: Number,
  },

  data() {
    return {
      store: useGlobalStore(),
      otjs: [],
      otjsReady: false,
      showOtjModal: false,
      isEditing: false,
      otjToEdit: undefined,
      showCoachNotesModal: false,
      coachNotes: '',
      showAddCommentsModal: false,
      otjToAddComments: {},
      addCommentsAction: false,
      totalTimeSpent: 0,
      plannedOTJHours: 0,
      selectedStatuses: [],
      otjStatuses: [
        { id: 'approved', name: 'Approved' },
        { id: 'pending', name: 'Pending' },
        { id: 'rejected', name: 'Referred' },
        { id: 'draft', name: 'Draft' },
      ],
      showExpandedTableModal: false,
      showOtjProgress: true,
    };
  },

  beforeMount() {
    this.$Loading.start();
    this.getOtjLogs();
  },

  watch: {
    programmeId(val) {
      if (val) {
        this.getOtjLogs();
      }
    },
  },

  computed: {
    useNewOtjProgressChart() {
      return this.$featureFlags.isEnabled('new-otj-progress-chart');
    },

    filteredOtjs() {
      const otjsWithoutResubmitted = this.otjs.filter(otj => !otj.has_been_resubmitted);
      if (this.selectedStatuses.length === this.otjStatuses.length || this.selectedStatuses.length === 0) {
        return otjsWithoutResubmitted;
      }
      return otjsWithoutResubmitted.filter(otj => this.selectedStatuses.includes(otj.status.toLowerCase()));
    },

    kableHeaders() {
      const headers = {
        date: {
          name: 'Date',
          type: 'date',
          sortable: true,
        },
        start_time: {
          name: 'Start',
          type: 'timestamp',
          sortable: false,
        },
        end_time: {
          name: 'End',
          type: 'timestamp',
          sortable: false,
        },
        time_spent: {
          name: 'Duration',
          type: 'string',
          sortable: true,
          displayAs: timeSpent => this.formatTimeSpent(timeSpent),
        },
        status: {
          name: 'Status',
          type: 'pill',
          sortable: true,
          options: {
            type: status => {
              const statusMap = {
                approved: 'success',
                referred: 'warning',
                rejected: 'danger',
                pending: 'primary',
                draft: 'neutral',
              };
              return statusMap[status.toLowerCase()] || 'neutral';
            },
          },
        },
        learning_activity: {
          name: 'Activity',
          type: 'string',
          sortable: true,
        },
        description: {
          name: 'Description',
          type: 'string',
          sortable: true,
          filterable: true,
        },
        competencies: {
          name: 'Competencies',
          type: 'tags',
          filterable: true,
        },
        note: {
          name: 'Coach Notes',
          type: 'button',
          downloadAs: row => row || ' - ',
          options: {
            preset: 'open',
            disabled: row => row.status.toLowerCase() !== 'referred' && row.status.toLowerCase() !== 'approved',
            title: row => (row.status.toLowerCase() === 'referred' ? 'See reason for referral' : 'See feedback'),
            ariaLabel: row => (row.status.toLowerCase() === 'referred' ? 'See reason for referral' : 'See feedback'),
            callback: this.handleTableAction,
          },
        },
      };
      if (this.$permissions.hasPermission('manage_user_otjs')) {
        headers.edit = {
          name: 'Edit',
          type: 'button',
          downloadAs: () => ' - ',
          options: {
            preset: 'edit',
            variant: 'default',
            title: 'Edit O.T.J. entry',
            ariaLabel: 'Edit O.T.J. entry',
            callback: this.handleTableAction,
          },
        };
      }
      if (this.store.learnerSupportType === 'coach') {
        headers.approve = {
          name: 'Approve',
          type: 'button',
          downloadAs: () => ' - ',
          options: {
            preset: 'approve',
            title: 'Approve O.T.J. entry',
            ariaLabel: 'Approve O.T.J. entry',
            disabled: row => row.status.toLowerCase() !== 'pending',
            callback: this.handleTableAction,
          },
        };
        headers.refer = {
          name: 'Refer',
          type: 'button',
          downloadAs: () => ' - ',
          options: {
            preset: 'refer',
            title: 'Refer back O.T.J. entry',
            ariaLabel: 'Refer back O.T.J. entry',
            disabled: row => row.status.toLowerCase() !== 'pending',
            callback: this.handleTableAction,
          },
        };
      }
      return headers;
    },

    kableRows() {
      if (!this.otjsReady) {
        return [];
      }
      return sortObjectArray(this.filteredOtjs, 'created_at', true).map(otj => {
        const row = {
          id: otj.id,
          date: otj.start_time,
          start_time: otj.start_time,
          end_time: otj.end_time,
          time_spent: otj.time_spent,
          status: otj.status === 'rejected' ? 'Referred' : otj.status,
          learning_activity: otj.category,
          description: otj.learning_description,
          competencies: {
            tags: otj.competencies ? otj.competencies.map(c => c.label) : [],
          },
          note: otj.otj_comment || '-',
        };
        if (this.$permissions.hasPermission('manage_user_otjs')) {
          row.edit = '-';
        }
        if (this.store.learnerSupportType === 'coach') {
          row.approve = '-';
          row.refer = '-';
        }
        return row;
      });
    },

    canEditOtj() {
      // On dashboard can only edit someone else's OTJ if you have the required permission
      return this.$permissions.hasPermission('manage_user_otjs');
    },

    otjTableFilename() {
      const now = new Date();
      const exportDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const csid = compactFormatId(this.userId) || this.userId;
      return `${csid}-OTJ-${exportDate}.csv`;
    },
  },

  methods: {
    getOtjLogs() {
      this.$Loading.start();
      this.otjsReady = false; // Set to false before fetching to show loading state
      this.$logger.info('Fetching OTJ logs', { programmeId: this.programmeId });
      this.$http.get(`/api/curriculum/admin/programmes/${this.programmeId}/otj-entries/${this.userId}`)
        .then(response => {
          this.$logger.info('Successfully fetched OTJ logs', { programmeId: this.programmeId });
          this.otjs = response.data.otj_entries || [];
          this.totalTimeSpent = response.data.total_time_spent;
          this.plannedOTJHours = response.data.planned_hours;
        })
        .catch(err => {
          if (err.response.data.err === 'otj_entries_not_found') {
            this.$logger.info('No OTJ logs found', { programmeId: this.programmeId }, err);
          } else {
            this.$logger.autowarn('Error fetching OTJ logs', { programmeId: this.programmeId }, err);
            this.$ktoast.warning('Error fetching OTJ logs');
          }
          this.otjs = [];
        }).then(() => {
          this.otjsReady = true;
          this.$Loading.finish();
        });
    },

    handleTableAction(key, index, row, column) {
      if (column === 'edit') {
        this.otjToEdit = this.otjs.find(x => x.id === row.id);
        this.openEditOtjModal();
      } else if (column === 'note') {
        this.coachNotes = this.otjs.find(x => x.id === row.id).otj_comment;
        this.showCoachNotesModal = true;
      } else if (column === 'approve') {
        this.addCommentsAction = true;
        this.addComments(row.id);
      } else if (column === 'refer') {
        this.addCommentsAction = false;
        this.addComments(row.id);
      }
    },

    openEditOtjModal() {
      this.showOtjModal = true;
      this.isEditing = true;
    },

    setOtjStatus(otjId, value) {
      const otj = this.otjs?.find(o => o.id === otjId);
      if (!otj || !this.programmeId) {
        return;
      }
      const jsonPayload = {
        status: value ? 'approved' : 'rejected',
        programme_competency_ids: otj.competencies ? otj.competencies.map(c => c.id) : [],
        category: otj.category,
        learning_description: otj.learning_description,
        declared_in_working_hours: otj.declared_in_working_hours,
        programme_member_id: this.programmeMemberId,
        user_id: this.userId,
        start_time: this.formatDate(otj.start_time),
        end_time: this.formatDate(otj.end_time),
      };

      this.$logger.info('Setting OTJ status', { otjId }, true);
      this.$http.put(`/api/curriculum/admin/programmes/${this.programmeId}/otj-entries/${otjId}`, jsonPayload)
        .then(() => {
          this.$logger.info('OTJ status set', { otjId });
          this.getOtjLogs();
          this.showOtjModal = false;
        })
        .catch(err => {
          this.$logger.error('Error setting OTJ status', { otjId }, err);
          this.$ktoast.showError(err, true);
        });
    },

    addComments(otjId) {
      this.otjToAddComments = this.otjs?.find(otj => otj?.id === otjId);
      if (!this.otjToAddComments) {
        return;
      }
      this.showAddCommentsModal = true;
    },

    expandTable() {
      this.showExpandedTableModal = true;
    },
  },
};
</script>
